import classNames from 'classnames';
// temp code for drag and drop with file upload
import React, { PropsWithChildren, useCallback, useMemo, useRef } from 'react';

import StyledDropZone from './StyledDropZone';

const typeRegex = /^.*\/.*$/;
const extensionRegex = /^\.[^\s]+$/;

export type DIRECTION =
  | 'upper_left'
  | 'upper_right'
  | 'lower_left'
  | 'lower_right';
interface DropZoneProps {
  dargKey?: string;
  className?: string;
  addFiles: (files: File[], direction?: DIRECTION) => void;
  dropzoneText?: string;
  accept?: string;
  onDrop?: (e: React.DragEvent<HTMLElement>) => void;
}
const DropZone: React.FC<PropsWithChildren<DropZoneProps>> = ({
  dargKey,
  children,
  className,
  addFiles,
  accept,
  dropzoneText = 'Drop files here',
  onDrop,
}) => {
  const accepts = useMemo(() => accept?.split(',') || [], [accept]);
  const ref = useRef<HTMLElement>(null);
  // ISSUE-040 — a dropped file that matches no `accept` entry is IGNORED, and
  // that is now a recorded decision rather than an open question. A rejected
  // file is the expected outcome of a user action, not an unexpected error, so
  // it must not reach the global error sink (`src/util/errorSink.ts`), which
  // exists for failures no code path already surfaces. Telling the user needs
  // a notification surface this app does not have — there is no toast/snackbar
  // component, and building one was out of that issue's scope — so the only
  // feedback is that the file does not appear in the list. The two branches
  // that used to carry the question were empty loops, so deleting them changes
  // nothing; `DropZone.test.tsx` pins the accepted/rejected split.
  const attachFile = useCallback(
    (files: File[], direction: DIRECTION) => {
      let acceptedFiles = [] as File[];
      accepts.forEach((accept) => {
        if (typeRegex.test(accept || '')) {
          acceptedFiles = acceptedFiles.concat(
            files.filter((file) => file.type.startsWith(accept))
          );
        } else if (extensionRegex.test(accept || '')) {
          acceptedFiles = acceptedFiles.concat(
            files.filter((file) => file.name.endsWith(accept || ''))
          );
        }
        // for duplicated case .wav,audio/wav
        files = files.filter((file) => !acceptedFiles.some((f) => f === file));
      });
      addFiles(acceptedFiles, direction);
      ref.current?.classList.remove('selected');
    },
    [accepts, addFiles]
  );

  return (
    <StyledDropZone
      className={classNames('sup-file-content', className)}
      ref={ref}
      onDragLeave={(e) => {
        e.preventDefault();
        if (!ref.current?.contains(e.relatedTarget as Node)) {
          ref.current?.classList.remove('selected');
        }
      }}
      onDragOver={(e) => {
        e.preventDefault();
        const isFileDrag = e.dataTransfer?.types[0] === 'Files';
        isFileDrag && ref.current?.classList.add('selected');
      }}
      onDrop={(e) => {
        e.preventDefault();
        if (dargKey && e.dataTransfer?.getData(dargKey)) {
          return onDrop?.(e);
        }
        let files = Array.prototype.slice.call(e.dataTransfer.files);
        const { width, left, top, height } =
          e.currentTarget.getBoundingClientRect();
        const vertical = e.clientY - top > height / 2 ? 'lower' : 'upper';
        const horizontal = e.clientX - left > width / 2 ? 'right' : 'left';
        attachFile(files, `${vertical}_${horizontal}`);
      }}
    >
      {children}
      <p className="dropzone-text">{dropzoneText}</p>
    </StyledDropZone>
  );
};

export default DropZone;
